/**
 * Connection API service.
 * Provides operations for managing connections and listing available connector types.
 * Supports both user-scoped and group-scoped connections.
 *
 * API Endpoints (user-scoped):
 * - GET /connections/types - List available connector types
 * - GET /connections - List user's connections
 * - POST /connections - Create a new connection
 * - POST /connections/:id/authorize - Authorize a connection with parameters
 * - DELETE /connections/:id - Delete a connection (pass ?force=true to delete even if referenced by sandboxes)
 *
 * API Endpoints (group-scoped):
 * - GET /sandboxGroups/:groupId/connections - List group connections
 * - POST /sandboxGroups/:groupId/connections - Create a group connection
 * - GET /sandboxGroups/:groupId/connections/:id - Get a group connection
 * - DELETE /sandboxGroups/:groupId/connections/:id - Delete a group connection
 * - POST /sandboxGroups/:groupId/connections/:id/authorize - Authorize a group connection
 * - POST /sandboxGroups/:groupId/connections/:id/refresh - Refresh a group connection
 * - POST /sandboxGroups/:groupId/connections/:id/generateConsentLink - Generate consent link
 * - PUT /sandboxGroups/:groupId/connections/:id/policyRules - Update policy rules
 *
 * @module services/connectionService
 */

import { httpClient } from './httpClient';
import type {
  ConnectionTypeResponse,
  ConnectionResponse,
  CreateConnectionRequest,
  AuthorizeConnectionRequest,
  GenerateConsentLinkResponse,
  PolicyRule,
} from '@/types';

const ENDPOINT = '/connections';

/**
 * Connection Service
 * Encapsulates all connection API operations
 */
export const connectionService = {
  /**
   * List available connector types
   * @returns Promise<ConnectionTypeResponse[]> - Array of available connector types
   */
  async listTypes(): Promise<ConnectionTypeResponse[]> {
    try {
      const response = await httpClient.get<ConnectionTypeResponse[]>(`${ENDPOINT}/types`);
      return response.data;
    } catch (error) {
      console.error('[ConnectionService] Failed to fetch connector types:', error);
      throw error;
    }
  },

  /**
   * List user's connections
   * @returns Promise<ConnectionResponse[]> - Array of user's connections
   */
  async list(): Promise<ConnectionResponse[]> {
    try {
      const response = await httpClient.get<ConnectionResponse[]>(
        `${ENDPOINT}?includeSandboxIds=true`
      );
      return response.data;
    } catch (error) {
      console.error('[ConnectionService] Failed to fetch connections:', error);
      throw error;
    }
  },

  /**
   * Create a new connection
   * @param request - The connection creation request
   * @returns Promise<ConnectionResponse> - The created connection
   */
  async create(request: CreateConnectionRequest): Promise<ConnectionResponse> {
    try {
      const response = await httpClient.post<ConnectionResponse>(ENDPOINT, request);

      if (response.data && typeof response.data.id === 'string' && response.data.id.length > 0) {
        return response.data;
      }

      // If no response body but we have a Location header (201 Created pattern),
      // fetch the connection from the Location URL
      const locationHeader = response.headers?.location;
      if (locationHeader) {
        const getResponse = await httpClient.get<ConnectionResponse>(locationHeader);
        return getResponse.data;
      }

      // Fallback: throw error if we can't get the connection data
      throw new Error('Connection created but no response data or Location header received');
    } catch (error) {
      console.error('[ConnectionService] Failed to create connection:', error);
      throw error;
    }
  },

  /**
   * Authorize a connection with parameter values
   * Used for API key and other parameter-based connectors
   * @param id - The connection ID
   * @param request - The authorization request with parameter values
   * @returns Promise<ConnectionResponse> - The authorized connection
   */
  async authorize(id: string, request: AuthorizeConnectionRequest): Promise<ConnectionResponse> {
    const encodedId = encodeURIComponent(id);
    try {
      const response = await httpClient.post<ConnectionResponse>(
        `${ENDPOINT}/${encodedId}/authorize`,
        request
      );
      return response.data;
    } catch (error) {
      console.error('[ConnectionService] Failed to authorize connection:', id, error);
      throw error;
    }
  },

  /**
   * Delete a connection
   * @param id - The connection ID
   * @param force - When true, deletes even if sandboxes still reference the connection
   */
  async delete(id: string, force = false): Promise<void> {
    const encodedId = encodeURIComponent(id);
    const url = force ? `${ENDPOINT}/${encodedId}?force=true` : `${ENDPOINT}/${encodedId}`;
    try {
      await httpClient.delete(url);
    } catch (error) {
      console.error('[ConnectionService] Failed to delete connection:', id, error);
      throw error;
    }
  },

  /**
   * Generate a consent link for a connection
   * @param id - The connection ID
   * @param redirectUrl - Optional redirect URL after consent
   * @returns Promise<string> - The consent URL to redirect to
   */
  async generateConsentLink(id: string, redirectUrl?: string): Promise<string> {
    const encodedId = encodeURIComponent(id);
    try {
      const response = await httpClient.post<GenerateConsentLinkResponse>(
        `${ENDPOINT}/${encodedId}/generateConsentLink`,
        redirectUrl ? { redirectUrl } : {}
      );
      return response.data.consentLink;
    } catch (error) {
      console.error('[ConnectionService] Failed to generate consent link:', id, error);
      throw error;
    }
  },

  /**
   * Refresh a connection's state from Azure
   * @param id - The connection ID
   * @returns Promise<ConnectionResponse> - The refreshed connection
   */
  async refresh(id: string): Promise<ConnectionResponse> {
    const encodedId = encodeURIComponent(id);
    try {
      const response = await httpClient.post<ConnectionResponse>(
        `${ENDPOINT}/${encodedId}/refresh?includeSandboxIds=true`
      );
      return response.data;
    } catch (error) {
      console.error('[ConnectionService] Failed to refresh connection:', id, error);
      throw error;
    }
  },

  /**
   * Update policy rules on a connection
   * @param id - The connection ID
   * @param policyRules - The new policy rules
   * @returns Promise<ConnectionResponse> - The updated connection
   */
  async updatePolicyRules(
    id: string,
    policyRules: PolicyRule[],
    enabledToolGroups?: string[]
  ): Promise<ConnectionResponse> {
    const encodedId = encodeURIComponent(id);
    try {
      const response = await httpClient.put<ConnectionResponse>(
        `${ENDPOINT}/${encodedId}/policyRules`,
        { policyRules, enabledToolGroups }
      );
      return response.data;
    } catch (error) {
      console.error('[ConnectionService] Failed to update policy rules:', id, error);
      throw error;
    }
  },

  /**
   * Check if the current user has a ready github-copilot connection.
   */
  async getCopilotStatus(): Promise<{
    hasReadyCopilotConnection: boolean;
    needsReauthorization?: boolean;
  }> {
    const response = await httpClient.get<{
      hasReadyCopilotConnection: boolean;
      needsReauthorization?: boolean;
    }>(`${ENDPOINT}/copilotStatus`);
    return response.data;
  },

  /**
   * Generate a GitHub OAuth URL for linking a GitHub account to create a copilot connector.
   * Used by Entra ID users who need to authorize GitHub separately.
   */
  async linkGitHub(redirectUrl?: string): Promise<{ consentLink: string }> {
    const response = await httpClient.post<{ consentLink: string }>(`${ENDPOINT}/linkGitHub`, {
      redirectUrl: redirectUrl ?? window.location.href,
    });
    return response.data;
  },

  // ── Group-scoped connection methods ──────────────────────────────────

  /**
   * List connections in a sandbox group
   * @param sandboxGroupId - The sandbox group ID
   * @returns Promise<ConnectionResponse[]> - Array of group connections
   */
  async listForGroup(sandboxGroupId: string): Promise<ConnectionResponse[]> {
    const encodedGroupId = encodeURIComponent(sandboxGroupId);
    try {
      const response = await httpClient.get<ConnectionResponse[]>(
        `/sandboxGroups/${encodedGroupId}/connections?includeSandboxIds=true`
      );
      return response.data;
    } catch (error) {
      console.error('[ConnectionService] Failed to fetch group connections:', error);
      throw error;
    }
  },

  /**
   * Create a connection in a sandbox group
   * @param sandboxGroupId - The sandbox group ID
   * @param request - The connection creation request
   * @returns Promise<ConnectionResponse> - The created connection
   */
  async createForGroup(
    sandboxGroupId: string,
    request: CreateConnectionRequest
  ): Promise<ConnectionResponse> {
    const encodedGroupId = encodeURIComponent(sandboxGroupId);
    try {
      const response = await httpClient.post<ConnectionResponse>(
        `/sandboxGroups/${encodedGroupId}/connections`,
        request
      );

      if (response.data && typeof response.data.id === 'string' && response.data.id.length > 0) {
        return response.data;
      }

      const locationHeader = response.headers?.location;
      if (locationHeader) {
        const getResponse = await httpClient.get<ConnectionResponse>(locationHeader);
        return getResponse.data;
      }

      throw new Error('Connection created but no response data or Location header received');
    } catch (error) {
      console.error('[ConnectionService] Failed to create group connection:', error);
      throw error;
    }
  },

  /**
   * Delete a connection from a sandbox group
   * @param sandboxGroupId - The sandbox group ID
   * @param id - The connection ID
   * @param force - When true, deletes even if sandboxes still reference the connection
   */
  async deleteForGroup(sandboxGroupId: string, id: string, force = false): Promise<void> {
    const encodedGroupId = encodeURIComponent(sandboxGroupId);
    const encodedId = encodeURIComponent(id);
    const url = force
      ? `/sandboxGroups/${encodedGroupId}/connections/${encodedId}?force=true`
      : `/sandboxGroups/${encodedGroupId}/connections/${encodedId}`;
    try {
      await httpClient.delete(url);
    } catch (error) {
      console.error('[ConnectionService] Failed to delete group connection:', id, error);
      throw error;
    }
  },

  /**
   * Authorize a connection in a sandbox group
   * @param sandboxGroupId - The sandbox group ID
   * @param id - The connection ID
   * @param request - The authorization request with parameter values
   * @returns Promise<ConnectionResponse> - The authorized connection
   */
  async authorizeForGroup(
    sandboxGroupId: string,
    id: string,
    request: AuthorizeConnectionRequest
  ): Promise<ConnectionResponse> {
    const encodedGroupId = encodeURIComponent(sandboxGroupId);
    const encodedId = encodeURIComponent(id);
    try {
      const response = await httpClient.post<ConnectionResponse>(
        `/sandboxGroups/${encodedGroupId}/connections/${encodedId}/authorize`,
        request
      );
      return response.data;
    } catch (error) {
      console.error('[ConnectionService] Failed to authorize group connection:', id, error);
      throw error;
    }
  },

  /**
   * Refresh a connection in a sandbox group
   * @param sandboxGroupId - The sandbox group ID
   * @param id - The connection ID
   * @returns Promise<ConnectionResponse> - The refreshed connection
   */
  async refreshForGroup(sandboxGroupId: string, id: string): Promise<ConnectionResponse> {
    const encodedGroupId = encodeURIComponent(sandboxGroupId);
    const encodedId = encodeURIComponent(id);
    try {
      const response = await httpClient.post<ConnectionResponse>(
        `/sandboxGroups/${encodedGroupId}/connections/${encodedId}/refresh?includeSandboxIds=true`
      );
      return response.data;
    } catch (error) {
      console.error('[ConnectionService] Failed to refresh group connection:', id, error);
      throw error;
    }
  },

  /**
   * Generate a consent link for a connection in a sandbox group
   * @param sandboxGroupId - The sandbox group ID
   * @param id - The connection ID
   * @param redirectUrl - Optional redirect URL after consent
   * @returns Promise<string> - The consent URL
   */
  async generateConsentLinkForGroup(
    sandboxGroupId: string,
    id: string,
    redirectUrl?: string
  ): Promise<string> {
    const encodedGroupId = encodeURIComponent(sandboxGroupId);
    const encodedId = encodeURIComponent(id);
    try {
      const response = await httpClient.post<GenerateConsentLinkResponse>(
        `/sandboxGroups/${encodedGroupId}/connections/${encodedId}/generateConsentLink`,
        redirectUrl ? { redirectUrl } : {}
      );
      return response.data.consentLink;
    } catch (error) {
      console.error('[ConnectionService] Failed to generate consent link:', id, error);
      throw error;
    }
  },

  /**
   * Update policy rules on a connection in a sandbox group
   * @param sandboxGroupId - The sandbox group ID
   * @param id - The connection ID
   * @param policyRules - The new policy rules
   * @returns Promise<ConnectionResponse> - The updated connection
   */
  async updatePolicyRulesForGroup(
    sandboxGroupId: string,
    id: string,
    policyRules: PolicyRule[],
    enabledToolGroups?: string[]
  ): Promise<ConnectionResponse> {
    const encodedGroupId = encodeURIComponent(sandboxGroupId);
    const encodedId = encodeURIComponent(id);
    try {
      const response = await httpClient.put<ConnectionResponse>(
        `/sandboxGroups/${encodedGroupId}/connections/${encodedId}/policyRules`,
        { policyRules, enabledToolGroups }
      );
      return response.data;
    } catch (error) {
      console.error('[ConnectionService] Failed to update policy rules:', id, error);
      throw error;
    }
  },
};
